/** Erreur renvoyee par l'API, avec son code HTTP et son code applicatif. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
    // Le cookie de session est httpOnly : sans `same-origin`, fetch ne
    // l'enverrait pas et toutes les requetes reviendraient en 401.
    credentials: 'same-origin',
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const body = payload as { error?: string; code?: string } | null;
    throw new ApiError(response.status, body?.error ?? response.statusText, body?.code);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export type Goal = 'loss' | 'maintain' | 'gain';
export type Sex = 'female' | 'male' | 'other';

export interface User {
  id: number;
  email: string;
  displayName: string | null;
  timezone: string;
  startWeight: number | null;
  goal: Goal;
  reminderEnabled: boolean;
  /** Heure locale au format HH:MM. */
  reminderTime: string;
  /** Seul l'administrateur voit la file de validation. */
  isAdmin: boolean;
  /** Morphologie, nullable : elle affine le modele de depense. */
  birthYear: number | null;
  sex: Sex | null;
  heightCm: number | null;
}

export interface Entry {
  day: string;
  weight: number | null;
  steps: number | null;
  kcal: number | null;
  /** Seance de la veille. `null` = jamais renseigne, `false` = pas de seance. */
  gym: boolean | null;
}

export interface RegisterInput {
  email: string;
  password: string;
  displayName?: string;
  startWeight?: number | null;
  birthYear?: number | null;
  sex?: Sex | null;
  heightCm?: number | null;
}

/**
 * L'inscription rend soit un compte ouvert — c'est le cas de l'administrateur,
 * qui s'auto-approuve — soit une demande en attente, sans session.
 */
export type RegisterResult = { user: User } | { pending: true };

export const isPending = (result: RegisterResult): result is { pending: true } =>
  'pending' in result;

export interface AccountRequest {
  id: number;
  email: string;
  display_name: string | null;
  created_at: string;
}

export const authApi = {
  config: () => api.get<{ allowRegistration: boolean }>('/auth/config'),
  me: () => api.get<{ user: User }>('/auth/me'),
  login: (email: string, password: string) =>
    api.post<{ user: User }>('/auth/login', { email, password }),
  register: (input: RegisterInput) => api.post<RegisterResult>('/auth/register', input),
  logout: () => api.post<{ ok: true }>('/auth/logout'),
};

export const adminApi = {
  requests: () => api.get<{ requests: AccountRequest[] }>('/admin/requests'),
  approve: (id: number) => api.post<{ approved: number }>(`/admin/requests/${id}/approve`),
  reject: (id: number) => api.delete<{ rejected: number }>(`/admin/requests/${id}`),
};

export interface ProfilePatch {
  displayName?: string | null;
  startWeight?: number | null;
  goal?: Goal;
  timezone?: string;
  reminderEnabled?: boolean;
  reminderTime?: string;
  birthYear?: number | null;
  sex?: Sex | null;
  heightCm?: number | null;
}

export const profileApi = {
  update: (patch: ProfilePatch) => api.patch<{ user: User }>('/profile', patch),
};

export const entriesApi = {
  list: (from: string, to: string) =>
    api.get<{ entries: Entry[] }>(`/entries?from=${from}&to=${to}`),
  // PUT remplace la journee entiere : les trois mesures partent ensemble.
  save: (day: string, values: Omit<Entry, 'day'>) =>
    api.put<{ entry: Entry | null }>(`/entries/${day}`, values),
  remove: (day: string) => api.delete<{ entry: null }>(`/entries/${day}`),
  // Écriture en masse, transactionnelle côté serveur : un import à moitié
  // appliqué laisserait un historique que l'utilisateur ne saurait pas rattraper.
  import: (entries: Entry[]) => api.post<{ saved: number }>('/entries/import', { entries }),
};
